import { ErrorMessage, Form, Formik } from 'formik'
import { isEmpty } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import axios from 'axios'
import Modal from 'rsuite/Modal'
import CheckPicker from 'rsuite/CheckPicker'
import FormRadio from 'src/cms/helpers/components/forms/FormRadio'
import FormTextBox from 'src/cms/helpers/components/forms/FormTextBox'
import { StateParamsModel } from 'src/app/modules/common/Model'
import * as reportMenu from '../index'

type Props = {
  open: boolean
  params?: StateParamsModel
  handleClose: () => void
  actionType: string
  editSelectedData?: any
}

const FORM_VALIDATION = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  slug: Yup.string()
    .required('Slug is required')
    .matches(/^[a-z0-9-]+$/, 'Slug must be lowercase letters, numbers, and hyphens only'),
  embedded_id: Yup.string().required('Embedded ID is required'),
  status: Yup.string().required('Status is required'),
  roles: Yup.array().min(1, 'At least one role is required').required('Roles are required'),
})

const statusOptions = [
  { label: 'Active', value: 'Active' },
  { label: 'Inactive', value: 'Inactive' },
]

const AddReportMenu = ({ open, params, handleClose, actionType, editSelectedData }: Props) => {
  const dispatch = useDispatch()
  const formikRef = useRef<any>()
  const { loading, success } = useSelector((state: any) => state.reportMenu)
  const [roleOptions, setRoleOptions] = useState<{ label: string; value: string }[]>([])

  useEffect(() => {
    const API_URL = window.__RUNTIME_CONFIG__?.REACT_APP_API_URL
    axios
      .get(`${API_URL}/role`, { params: { page: 1, limit: 9999 } })
      .then((res) => {
        const roles = res?.data?.data?.role || []
        setRoleOptions(roles.map((r: any) => ({ label: r.name, value: r.name })))
      })
      .catch(() => { })
  }, [])

  useEffect(() => {
    if (!isEmpty(editSelectedData)) {
      formikRef.current?.setFieldValue('title', editSelectedData.title, false)
      formikRef.current?.setFieldValue('slug', editSelectedData.slug, false)
      formikRef.current?.setFieldValue('embedded_id', editSelectedData.embedded_id, false)
      formikRef.current?.setFieldValue(
        'status',
        editSelectedData.status === true ? 'Active' : 'Inactive',
        false
      )
      formikRef.current?.setFieldValue('roles', editSelectedData.roles || [], false)
    }
  }, [editSelectedData])

  useEffect(() => {
    if (success) {
      dispatch(reportMenu.actions.getReportMenuData(params))
      isEmpty(editSelectedData)
        ? toast.success('Report Menu added successfully')
        : toast.success('Report Menu updated successfully')
      dispatch(reportMenu.actions.resetReportMenu())
      handleClose()
    }
  }, [success])

  return (
    <div className='modal-container'>
      <Modal
        open={open}
        onClose={handleClose}
        className='w-75'
        backdrop='static'
        keyboard={false}
        enforceFocus={false}
        data-cy='modal'
      >
        <Modal.Header>
          <Modal.Title data-cy='modal-title'>{actionType} Report Menu</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Formik
              innerRef={formikRef}
              initialValues={{
                title: '',
                slug: '',
                embedded_id: '',
                status: '',
                roles: [] as string[],
              }}
              validationSchema={FORM_VALIDATION}
              onSubmit={(values) => {
                const formData = {
                  dashboard_title: values.title,
                  dashboard_slug: values.slug,
                  embed_uuid: values.embedded_id,
                  is_active: values.status === 'Active',
                  roles: values.roles,
                }
                if (!isEmpty(editSelectedData)) {
                  dispatch(reportMenu.actions.updateReportMenu(formData, editSelectedData?.id))
                } else {
                  dispatch(reportMenu.actions.addReportMenu(formData))
                }
              }}
            >
              {({ touched, handleChange, errors, values, setFieldValue, setFieldTouched }) => (
                <Form>
                  <div className='card-body border-top pt-5'>
                    <div className='row'>
                      <div className='col-md-12 col-xs-12'>
                        <FormTextBox
                          type='text'
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          placeholder='Enter Report Title'
                          label='Title'
                          name='title'
                          onChange={(e: any) => {
                            handleChange(e)
                            const slug = e.target.value
                              .toLowerCase()
                              .replace(/\s+/g, '-')
                              .replace(/[^a-z0-9-]/g, '')
                            setFieldValue('slug', slug, true)
                          }}
                          errors={errors}
                          touched={touched}
                          required={true}
                        />
                      </div>
                      <div className='col-md-6 col-xs-12'>
                        <FormTextBox
                          type='text'
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          placeholder='e.g. loan-report'
                          label='Slug'
                          name='slug'
                          disabled
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          required={true}
                        />
                      </div>
                      <div className='col-md-6 col-xs-12'>
                        <FormTextBox
                          type='text'
                          labelClassName='col-md-12'
                          containerClassName='col-md-12'
                          placeholder='e.g. 9d948bb5-a645-418a-898a-08139af20fa1'
                          label='Embedded ID'
                          name='embedded_id'
                          onChange={handleChange}
                          errors={errors}
                          touched={touched}
                          required={true}
                        />
                      </div>
                      <div className='col-md-12 col-xs-12 pb-2'>
                        <label className='form-label fw-bolder text-dark fs-6 required'>
                          Roles
                        </label>
                        <CheckPicker
                          data={roleOptions}
                          value={values.roles}
                          onChange={(val) => {
                            setFieldValue('roles', val)
                            setFieldTouched('roles', true)
                          }}
                          placeholder='Select Roles'
                          searchable={true}
                          block
                        />
                        {touched.roles && (errors.roles as any) && (
                          <div className='fv-plugins-message-container text-danger fw-bolder small text-start mt-1'>
                            {errors.roles as any}
                          </div>
                        )}
                      </div>
                      <div className='col-md-4 col-xs-12'>
                        <label className='form-label fw-bolder text-dark fs-6 required'>
                          Status
                        </label>
                        <div className='d-flex ms-5'>
                          {statusOptions.map((status) => (
                            <FormRadio
                              key={status.value}
                              containerClassName=''
                              label='Select Status'
                              name='status'
                              type='radio'
                              onChange={handleChange}
                              errors={errors}
                              touched={touched}
                              required={true}
                              checkBoxText={status.label}
                              value={status.value}
                              defaultValue={values.status}
                            />
                          ))}
                        </div>
                        <div className='fv-plugins-message-container text-danger fw-bolder small text-start mt-1'>
                          <ErrorMessage
                            name='status'
                            component='div'
                            className='field-error-message'
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className='d-flex justify-content-end px-9'>
                    <button
                      type='submit'
                      disabled={loading}
                      className='btn btn-primary btn-sm ms-3'
                      data-cy='modal-submit'
                    >
                      Save
                    </button>
                    <button
                      type='button'
                      onClick={handleClose}
                      className='btn btn-secondary btn-sm ms-3'
                      data-cy='modal-cancel'
                    >
                      Cancel
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  )
}

export default AddReportMenu
